/* eslint-disable no-return-assign */
import * as yup from 'yup';

import {
  idSchema,
  nullableIdSchema,
  stringSchema,
  numberSchema,
  idArraySchema,
} from './validationUtil';

function emptyStringToNull(value, originalValue) {
  if (typeof originalValue === 'string' && originalValue === '') {
    return null;
  }
  return value;
}

export const VoucherSchema = yup.object().shape({
  VoucherId: stringSchema,
  VoucherTypeId: numberSchema.required().label('Voucher type'),
  VoucherNumber: stringSchema.required().label('Voucher number'),
  Description: stringSchema,
  VoucherDate: stringSchema.required().label('Voucher date'),
  ContactId: idSchema.required().label('Contact'),
  DueDate: stringSchema.label('Due date').when('VoucherTypeId', vTypeId => {
    // If Voucher Type is DP then due date should be null
    if (vTypeId > 4) {
      return yup.string().nullable(true);
    }
    return yup.string().required();
  }),
  StatusId: nullableIdSchema.required().label('Status'),
  AccountId: idSchema
    .required()
    .label('Account')
    .typeError('An account must be selected'),
});

export const VoucherTransactionSchema = yup.object().shape({
  VoucherTransactionId: stringSchema.label('Voucher transaction'),
  VoucherId: stringSchema.required(),
  CategoryId: stringSchema
    .required()
    .label('Transaction category')
    .typeError('A category must be selected'),
  AccountId: idSchema
    .required()
    .label('Account')
    .typeError('An account must be selected'),
  Quantity: numberSchema
    .label('Quantity')
    .typeError('Please enter a quantity')
    .required(),
  Price: numberSchema
    .label('Price')
    .typeError('Please enter a price')
    .required(),
  Description: stringSchema
    .label('Description')
    .when('isIntegrated', isIntegrated => {
      if (isIntegrated) {
        return yup.string().required();
      }
      return yup.string();
    }),
});

export const PaymentSchema = yup.object().shape({
  PaymentId: stringSchema,
  ContactId: nullableIdSchema.required().label('Contact'),
  PaymentDate: stringSchema.required().label('Payment date'),
  DepositDate: stringSchema.required().label('Deposit date'),
  ClearedDate: stringSchema.required().label('Cleared date'),
  Amount: numberSchema.required().label('Amount'),
  Description: stringSchema,
  StatusId: nullableIdSchema.required().label('Payment status'),
  TypeId: stringSchema.required().label('Payment type'),
  TranType: stringSchema.required().label('Transaction type'),
  // IsCleared: stringSchema.required(),
  AccountId: idSchema.required().label('Account'),
});

export const ContactSchema = yup.object().shape({
  ContactId: nullableIdSchema,
  ContactTypeId: nullableIdSchema.label('Contact type'),
  Name: stringSchema.required(),
  FirstName: stringSchema.label('First name'),
  LastName: stringSchema.label('Last name'),
  Email: stringSchema.email(),
  Phone: stringSchema,
  // IsUpdatingExtSys: numberSchema.required(),
});

export const DealSchema = yup.object().shape({
  DealId: stringSchema,
  Title: stringSchema.required().label('Title'),
  Description: stringSchema,
  ContractDate: stringSchema.required().label('Contract date'),
  TransactionDate: stringSchema.required().label('Transaction date'),
  BillableDate: stringSchema.required().label('Billable date'),
  PayableDate: stringSchema.required().label('Payable date'),
  ApprovedBy: stringSchema,
  StatusId: nullableIdSchema.required().label('Status'),
});

export const DealTransactionSchema = yup.object().shape({
  DealTranId: stringSchema,
  DealId: stringSchema.required().label('Deal'),
  Type: stringSchema
    .label('Transaction type')
    .typeError('A transaction type must be selected'),
  CategoryId: stringSchema
    .required()
    .label('Transaction category')
    .typeError('A category must be selected'),
  ContactId: stringSchema
    .required()
    .label('Contact')
    .typeError('A contact must be selected'),
  Price: numberSchema
    .label('Price')
    .typeError('Please enter a price')
    .required(),
  Quantity: numberSchema
    .required()
    .label('Quantity')
    .typeError('Please enter a quantity'),
  Description: stringSchema.label('Description'),
  DebitAccount: stringSchema
    .required()
    .label('Debit account')
    .typeError('A debit account must be selected'),
  CreditAccount: stringSchema
    .required()
    .label('Credit account')
    .typeError('A credit account must be selected'),
});

export const DealTransactionSchemaCSV = yup.object().shape({
  DealTitle: stringSchema.required().label('Deal Title'),
  TranType: stringSchema.label('Transaction type'),
  TranCategoryCode: stringSchema.required().label('Transaction category code'),
  ContactName: stringSchema.required().label('Contact'),
  Price: numberSchema.required().label('Price'),
  Quantity: numberSchema.required().label('Quantity'),
  Description: stringSchema,
  DebitAccountCode: stringSchema.required().label('Debit account'),
  CreditAccountCode: stringSchema.required().label('Credit account'),
});

export const DealSchemaCSV = yup.object().shape({
  Title: stringSchema,
  Description: stringSchema,
  ContractDate: stringSchema.required().label('Contract date'),
  TransactionDate: stringSchema.required().label('Transaction date'),
  BillableDate: stringSchema.required().label('Billable date'),
  PayableDate: stringSchema.required().label('Payable date'),
  ApprovedBy: stringSchema,
  StatusName: stringSchema.required().label('Status'),
});

const DTEnumMapping = {
  string: stringSchema,
  dollar: numberSchema,
  date: stringSchema,
  enum: stringSchema,
  contact: stringSchema,
  agent: stringSchema,
  tranCategory: nullableIdSchema,
};

export const DealTemplateSchema = templateItems => {
  const schema = templateItems.reduce((agg, { name, type, required }) => {
    agg[name] = DTEnumMapping[type];
    if (required) agg[name] = agg[name].required();
    return agg;
  }, {});
  return yup.object().shape(schema);
};

export const ContactAgentSchema = yup.object().shape({
  ContactId: stringSchema,
  SaleSplit: numberSchema
    .required()
    .max(100)
    .min(0),
  RentalSplit: numberSchema
    .required()
    .max(100)
    .min(0),
});

export const AccountSchema = yup.object().shape({
  accountCode: stringSchema.required(),
  accountName: stringSchema.required(),
  type: stringSchema.required(),
  statusId: numberSchema.required(),
  bankAccountType: stringSchema,
});

export const SplitSchema = yup.object().shape({
  rentalSplit: numberSchema.required().min(0),
  saleSplit: numberSchema.required().min(0),
});

export const InviteSchema = yup.object().shape({
  email: stringSchema
    .email('Invalid Email')
    .required()
    .label('Email'),
  roles: idArraySchema
    .min(1)
    .required()
    .label('Roles'),
  contactId: stringSchema.required().label('Contact'),
});
